// Launch copy for /developers and /haunt (wave 11), transcribed from docs/launch/ (developer-page.md, open-haunt.md,
// open-haunt-howto.md) as written. Rules from docs/launch/README.md:
// - `{{track: fact}}` marks a fact another track supplies. A line whose placeholder isn't filled stays off the page
//   (`fill` returns null), so placeholders never reach the DOM.
// - `[[ … ]]` is an optional clause inside a line: it's dropped while its own placeholder is unfilled, and the rest of
//   the line still shows (the speed footnote keeps its date and method without the machine's name).
// - `[[?gate| … ]]` shows only when FACTS[gate] is set (a block that waits on something other than a name), and
//   `[[?gate| … || … ]]` picks the second text while the gate is unset (game names until the owner OKs them).
// - Inline: **bold**, `code`, *emphasis*. test/launch-copy.test.ts checks every line that renders against the docs,
//   word for word, and the words public copy never uses.
// To fill a fact, set it in FACTS (or pass it at render time for facts the site reads live).

export type Facts = Record<string, string | undefined>;

/**
 * Facts that are settled (filled) and the ones still open (undefined). Live facts are added by the pages:
 * `catalogue: listed count` (the catalogue), `world: plaza night time` (the rooms calendar), `rows: minutes` (the quest).
 */
export const FACTS: Facts = {
  // CONTRACTS.md "Unlocks": each of three haunts needs >= 180 s of active play
  "rows: minutes": "3",
  // claims.md S9, from the launch track (4baf110): system_profiler on the machine the headline runs ran on
  "engine: test Mac model": "a Mac mini (M4, 24 GB)",
  // open: claim only after measuring cold vs prefetched load (claims.md S6)
  "measured: prefetch": undefined,
  // the owner chose thrdm.app (launch track, fa8dba6). Every deploy line stays held until prod confirms its
  // certificate (claims.md P4); then set this to "thrdm.app" and the hosting line and "New game?" offer appear
  "builds-domain": undefined,
  // open: the cut-off's time zone (claims.md H10, owner)
  "owner: time zone, suggest \"anywhere on Earth\"": undefined,
  "owner: CC0 only, or the creator's choice of store licence": undefined,
  "owner: how the stage set is picked, e.g. \"as many as fit, the row's order\"": undefined,
  "owner/rows: a creator badge, only if one is built by Fri 23": undefined,
  // naming the test games in public (claims.md S10): the owner OK'd it on 2026-10-21 with the game art on the
  // developer page. Unset it to fall back to "a WebGL city scene", "a driving game", "a flight game" and so on
  "owner-names": "yes",
  // gates: the MCP has a public install path (claims.md H3); the beginner guide can be followed end to end (H1–H3);
  // the owner confirmed old games and jam games count
  "mcp-public": undefined,
  "guide-public": undefined,
  "owner-confirmed-old-games": undefined,
};

const PLACEHOLDER = /\{\{([^{}]+)\}\}/g;

/** `text` with its facts filled in, or null while any placeholder (outside an optional clause) is unfilled. */
export function fill(text: string, facts: Facts = FACTS, showHeld = false): string | null {
  if (showHeld) return text.replace(/\[\[\?[^|\]]+\|([^\]]*?)(?:\|\|[^\]]*)?\]\]/g, "$1").replace(/\[\[|\]\]/g, "");
  const out = text
    .replace(/\[\[\?([^|\]]+)\|([^\]]*?)(?:\|\|([^\]]*))?\]\]/g, (_, gate: string, seg: string, other?: string) => (facts[gate.trim()] ? seg : (other ?? "")))
    .replace(/\[\[([^\]]*)\]\]/g, (_, seg: string) => fillPlain(seg, facts) ?? "");
  return fillPlain(out, facts);
}

function fillPlain(text: string, facts: Facts): string | null {
  let missing = false;
  const out = text.replace(PLACEHOLDER, (_, key: string) => {
    const v = facts[key.trim()];
    if (v === undefined || v === "") missing = true;
    return v ?? "";
  });
  return missing ? null : out;
}

/** The plain words of a line as the docs have them (markup and clause brackets removed), for the copy test. */
export function plain(text: string): string {
  return text
    .replace(/\[\[\?[^|\]]+\|([^\]]*?)\|\|[^\]]*\]\]/g, "$1")
    .replace(/\[\[\?[^|\]]+\|/g, "")
    .replace(/\[\[|\]\]/g, "")
    .replace(/\*\*|`|\*/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export type Inline = { t: "text" | "b" | "code" | "em"; s: string };

/** **bold**, `code` and *emphasis* into runs (the page builds DOM from them, never HTML). */
export function inline(text: string): Inline[] {
  const out: Inline[] = [];
  const re = /\*\*([^*]+)\*\*|`([^`]+)`|\*([^*]+)\*/g;
  let last = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) out.push({ t: "text", s: text.slice(last, m.index) });
    out.push(m[1] !== undefined ? { t: "b", s: m[1] } : m[2] !== undefined ? { t: "code", s: m[2] } : { t: "em", s: m[3]! });
    last = re.lastIndex;
  }
  if (last < text.length) out.push({ t: "text", s: text.slice(last) });
  return out;
}

// ---- the pages' copy ------------------------------------------------------------------------------------------------

export interface Action {
  text: string;
  href: string;
  /** shown only when this line fills (a button that waits on the same fact as its block) */
  when?: string;
}

export interface Block {
  id?: string;
  heading?: string;
  paras?: string[];
  list?: string[];
  steps?: string[];
  actions?: Action[];
  /** the whole block waits on this gate or placeholder line */
  when?: string;
  /** a code block (the guide) */
  code?: string[];
}

export const DEV_HERO = {
  title:
    "[[?owner-names|Nightrider's WebGL city||A WebGL city scene]]: 16.4 ms a frame in our native engine with a shader pass we verified for that game, 22.1 ms in Chrome.",
  sub: "Same game code, our test Mac, 18 October. Below, all six of our test games, one at a time.",
  actions: [
    { text: "See the numbers", href: "#numbers" },
    { text: "Get your game on Banger", href: "#get" },
  ] as Action[],
};

/** Two rows per game, frame rate (or time) then load, game by game: the page draws one game per beat. */
export const DEV_TABLE = {
  head: ["Game", "Our engine", "Chrome", ""],
  rows: [
    ["[[?owner-names|Nightrider||A WebGL city scene]], frame time", "16.4 ms", "22.1 ms", "1.35×"],
    ["[[?owner-names|Nightrider||A WebGL city scene]], load", "1.83 s", "2.29 s", "1.25×"],
    ["The Plaza, frame rate", "44.4 fps", "38.8 fps", "1.14×"],
    ["The Plaza, load", "1.59 s", "2.37 s", "1.49×"],
    ["[[?owner-names|Punk Drive||A driving game]], frame rate", "230.6 fps", "224.4 fps", "1.03×"],
    ["[[?owner-names|Punk Drive||A driving game]], load", "5.61 s", "6.12 s", "1.09×"],
    ["[[?owner-names|Dystopia||A flight game]], frame rate", "44.0 fps", "37.3 fps", "1.18×"],
    ["[[?owner-names|Dystopia||A flight game]], load", "18.53 s", "20.36 s", "1.10×"],
    ["[[?owner-names|Gigacity||A sky-city game]], frame rate", "75.1 fps", "63.2 fps", "1.19×"],
    ["[[?owner-names|Gigacity||A sky-city game]], load", "1.47 s", "1.59 s", "1.09×"],
    ["[[?owner-names|Off Road Earth||An off-road game]], frame rate", "144.1 fps", "66.4 fps", "2.17×"],
    ["[[?owner-names|Off Road Earth||An off-road game]], load", "2.47 s", "3.53 s", "1.43×"],
  ],
};

export const DEV_BLOCKS: Block[] = [
  {
    id: "today",
    heading: "Where your game runs today",
    paras: [
      "In the Banger launcher today, your game runs in a Chromium-based player, as fast as it runs in Chrome. Our native engine isn't open to other games yet. The numbers below are our own tests.",
    ],
  },
  {
    id: "numbers",
    heading: "The numbers",
    paras: ["Our native engine against Chrome on the same Mac, game code unchanged, every Chrome round right after ours."],
  },
];

export const DEV_NUMBERS_AFTER = [
  "Across the six games: **1.30× Chrome's frame rate, 1.23× its load speed.**",
  "Every game is at or above Chrome on both. Our own bar is 1.25× on average on both, and we're not there yet: load is at 1.23×, and without [[?owner-names|Off Road Earth||the off-road game]], which hits its own 144 fps cap in our engine, frame rate is 1.17×. This table changes when the numbers do.",
];

export const DEV_NUMBERS_FOOT =
  "Measured 18 Oct 2026[[ on {{engine: test Mac model}}]], Chrome 155 with its frame cap off. Frame rate: median of three interleaved runs. Load: warm start, every file already cached on both sides, to the first frame that draws at least half the scene's usual draw calls. [[?owner-names|Nightrider's||The city scene's]] frame time is a fixed replay (Chrome's from 17 Oct) with a shader pass verified for that game (without it: 1.00×). [[?owner-names|Gigacity and Off Road Earth||The sky-city and off-road games]] ran in Chrome on their live sites for frame rate. The Plaza is ours.";

export const DEV_BLOCKS_AFTER: Block[] = [
  {
    id: "updates",
    heading: "Ship an update. Every fan hears about it.",
    paras: [
      "When your game changes, Banger notices. It marks your game **Updated** in players' libraries, adds it to \"Just updated\", and tells everyone who hearted it. You don't add any code.",
      "We only show Updated after we've seen the change twice, so a stray rebuild doesn't spam your players. If your page changes on every load, we can't tell a real update from noise: add a `thrdm.json` file next to your game and we'll follow that instead. It's also where release notes go, or write them in your dashboard.",
      "Your update is ready before they press Play. {{measured: prefetch}}",
    ],
  },
  {
    id: "friends",
    heading: "One click to play. Friends already there.",
    paras: [
      "Your game sits in your players' libraries, one click from full screen. No tabs, no hunting for the link.",
      "Friends see who's playing and join them from the overlay. Your game doesn't need any code for it.",
    ],
  },
  {
    id: "hosting",
    heading: "Free hosting, and numbers you don't get from a URL",
    paras: [
      "Push a build with `thrdm deploy`. We host it free, every version stays, and you can roll back in one command. It's live at its own link right away (`your-game.{{builds-domain}}`), and listed on Banger once it passes review.",
      "Your dashboard shows players, sessions, how long a session lasts, who comes back the next day and a week later, and how long players keep playing before they stop.",
    ],
  },
  {
    id: "before",
    heading: "Before launch, and after",
    paras: [
      "**Waitlists.** Not ready yet? List it as Coming soon. Players tap Notify me and hear the day it's out.",
      "**Clips.** Players save the last 30 seconds with one key. Every clip has a Play button that opens your game.",
    ],
  },
  {
    id: "cost",
    heading: "What it costs",
    paras: [
      "Free to list. Free to host.",
      "Selling is off at launch, so everything on Banger is free for now. When selling switches on, creators keep 80%. No date yet.",
    ],
  },
  {
    id: "rewrite",
    heading: "Nothing to rewrite",
    paras: [
      "Any web engine: three.js, Babylon, PlayCanvas, Godot, Unity web builds. No SDK required, and if you use ours, it's MIT: free to use in free or paid games. Your game keeps its own site; Banger is one more place players find it and come back to it.",
    ],
  },
];

/** "Get on Banger": three offers, each with its button. */
export const DEV_GET: { para: string; action: Action; when?: string }[] = [
  {
    para: "**Already listed?** We've listed {{catalogue: listed count}} web games, each linking to its own site. If one is yours, claim it to get stats, update notes and a waitlist, or remove it in one click.",
    action: { text: "Find your game", href: "/browse" },
  },
  {
    // held with the hosting section until the builds domain is live
    para: "**New game?** `thrdm deploy ./dist`. It's live at its own link, and listed once it passes review.",
    action: { text: "Read the guide", href: "/haunt/guide" },
    when: "{{builds-domain}}",
  },
  {
    para: "**Made something spooky?** Open Haunt runs 23 October – 6 November. Ship it and it's in the Halloween row.",
    action: { text: "Open Haunt", href: "/haunt" },
  },
];

export const DEV_FAQ: [string, string][] = [
  ["Is my game faster on Banger?", "Not yet. In the launcher it runs as fast as it does in Chrome. The numbers at the top are from our native engine, on our test games."],
  ["Do I have to change my game?", "No. A listed game is your game at its own address. Nothing to install, no SDK."],
  [
    "Do you host my game without asking?",
    "No. Until you claim it, the launcher opens your game from your own site, and your code isn't changed. We never upload your build without you.",
  ],
  ["Who can publish?", "Creators are 18 or older. Players are 13 or older."],
  ["What does Banger take?", "Nothing while selling is off. When it switches on, creators keep 80% of net in the first year."],
];

// ---- Open Haunt (open-haunt.md) ----------------------------------------------------------------------------------

export const HAUNT_HERO = {
  title: "Open Haunt",
  lead: "Make something spooky. If you're 18 or older and it passes review, it's in the Halloween row.",
  dates: "23 October – 6 November. Free. No jury.",
  actions: [
    { text: "Start with The Haunt template", href: "/haunt/guide", when: "[[?guide-public|yes]]" },
    { text: "How it works", href: "#how" },
  ] as Action[],
};

export const HAUNT_HOW: Block = {
  id: "how",
  heading: "How it works",
  steps: [
    "**Make something spooky.** A game, a world to walk around, or a 3D asset. Start from our haunt template and free Halloween pack, or bring your own.",
    "**Ship it on Banger** by 6 November. Games and worlds go up with `thrdm deploy`, assets with `thrdm publish`.",
    "**It joins the Halloween row** once it passes review. Players order the row with their hearts and playtime.",
    "**Haunt night, 7 November:** we play the week's haunts live on the stage in the Plaza[[ ({{world: plaza night time}})]].",
  ],
};

export const HAUNT_PUMPKIN: Block = {
  id: "pumpkin",
  heading: "Visit three haunts, light up your pumpkin",
  paras: [
    "The pumpkin head is free for everyone. Play any three games in the Halloween row for {{rows: minutes}} minutes each and yours lights up: a candlelit pumpkin head, glowing from inside.",
    "Any three count, so players try haunts further down the row too. (Fortnite's Halloween quests count any creator's horror island; we borrowed the idea.)",
  ],
};

export const HAUNT_FREE: Block = {
  id: "free",
  heading: "Free to make, free to get",
  // every piece named here passes `store build` + `check --render` (docs/build/haunt.md on the haunt branch)
  paras: [
    "There's no fee to upload or to list, and selling is off at launch, so every haunt is free to play and every Halloween asset is free to use. Our Halloween pack is free too: pumpkins you can carve, gravestones, dead trees, fences, a haunted-house kit, candles, a lantern, fog, bats and a ghost, plus a pumpkin head, a witch hat and a skeleton hoodie for avatars.",
  ],
  actions: [{ text: "Get the Halloween pack", href: "/store?collection=halloween" }],
};

export const HAUNT_OPEN: Block = {
  id: "open",
  heading: "The open way to do Halloween",
  paras: ["Roblox teased its Halloween event this year, The Hollow, with nine names. Open Haunt has room for everyone who ships."],
};

export const HAUNT_AGENT: Block = {
  id: "agent",
  heading: "Built with an AI agent?",
  when: "[[?mcp-public|yes]]",
  paras: [
    "The haunt template comes with instructions for AI coding agents. Open it in your agent, say \"make my haunted house\", and deploy through the thrdm MCP (`thrdm mcp`). Nothing uploads until you say yes.",
  ],
  actions: [{ text: "Beginner guide", href: "/haunt/guide" }],
};

export const HAUNT_RULES: { heading: string; list: string[] }[] = [
  {
    heading: "Dates",
    list: [
      "Ship between Fri 23 October and Fri 6 November[[, 23:59 {{owner: time zone, suggest \"anywhere on Earth\"}}]].",
      "Haunt night in the Plaza: Sat 7 November.",
      "The Halloween row stays up until Tue 10 November. Your haunt stays on Banger after that; only the row ends.",
    ],
  },
  {
    heading: "Who",
    list: ["Creators are 18 or older. Players are 13 or older.", "Teams are welcome. One person 18+ publishes; credit everyone in the description."],
  },
  {
    heading: "What counts",
    list: [
      "A game, a world or a 3D asset that's spooky.",
      "[[?owner-confirmed-old-games|It doesn't have to be new. It has to be on Banger by 6 November. Jam games are welcome.]]",
      "AI tools are fine. You're responsible for what you ship.",
    ],
  },
  {
    heading: "Spooky, not gory",
    list: [
      "No graphic gore, nothing sexual, no hate. Players can be 13.",
      "You made it, or you have the rights to everything in it. No characters or worlds from other people's games, films or books.",
      "Review checks every haunt. If it's turned down, you get the reason and can fix it and send it again.",
    ],
  },
  {
    heading: "Assets",
    list: [
      "Static only for now: a GLB, its textures and a manifest. No code inside assets yet.",
      // the doc's next line asks the owner whether to publish a cap; it isn't page copy
      "Size: each asset declares its own budget (triangles, draw calls, texture size) and review holds it to that.",
      "Free to use in free or paid games: the haunt template and Halloween pack are CC0, the thrdm SDK and asset runtime are MIT.[[ Licence for your own assets: {{owner: CC0 only, or the creator's choice of store licence}}.]]",
    ],
  },
  {
    heading: "Free",
    list: ["No fee to upload, list or join.", "Selling is off at launch, so haunts and Halloween assets are free to play and to use."],
  },
  {
    heading: "A showcase, not a contest",
    list: [
      "No prizes, no judges, no winners.",
      "What you get: a place in the Halloween row, the chance to be played on stage on Haunt night, and your game in front of players who come to light up their pumpkin.",
      "Which haunts get played on stage: {{owner: how the stage set is picked, e.g. \"as many as fit, the row's order\"}}.",
      "{{owner/rows: a creator badge, only if one is built by Fri 23}}",
    ],
  },
  {
    heading: "Leaving",
    list: ["You can take your haunt down at any time."],
  },
];

// ---- the beginner guide (open-haunt-howto.md): held as a whole until "guide-public" ------------------------------
// Its own header says not to publish it until install, sign-in and the builds domain are live (claims.md H1–H3).
// The text follows the launch track's newest draft (claude/build11-launch-762483, 156ea46).

export const GUIDE: Block[] = [
  {
    heading: "Make a haunted house in an evening",
    paras: [
      "**The Haunt** is a small 3D game that runs in a browser. You walk through a graveyard at night, follow the jack-o'-lanterns up to an old house, and go inside, where something is waiting. Change it, then put it on Banger. Free, for anyone 18 or older.",
      "You don't need to know 3D. If you can edit a list in a text file, you can make this your own.",
    ],
  },
  {
    heading: "1. What you need",
    list: ["**Node.js 20 or newer**, from nodejs.org (pick \"LTS\").", "**A code editor.** VS Code is free.", "**A terminal:** Terminal on a Mac, PowerShell on Windows."],
  },
  {
    heading: "2. Get the template and run it",
    paras: ["Download `haunt-template.zip` from {{haunt: banger.sh/downloads/haunt-template.zip}} and unzip it. Then, in a terminal inside the folder:"],
    code: ["npm install", "npm run dev"],
  },
  {
    paras: [
      "Open the address it prints (like `http://localhost:5173/`) in Chrome and click **Click to enter**. Mouse to look, **W A S D** to walk, **Shift** to run, **Esc** to let go of the mouse. Every time you save a file, the page reloads.",
    ],
  },
  {
    heading: "3. Make it yours",
    paras: ["Open **`src/haunt.js`**. It's a list of everything in the scene, one line per thing:"],
    code: ['{ asset: "pumpkin", at: [-1.5, 11], params: { face: "classic" }, turn: 25, solid: 0.3 },'],
  },
  {
    paras: [
      "Change `at` to move it, `turn` to spin it, `params` to change how it looks, and `seed` for a different tree, stone or swarm. The full list of params is in the template's `AGENTS.md`.",
      "Everything comes from Banger's free Halloween pack. The pack and the template are CC0 and the thrdm SDK and asset runtime are MIT, so they're free to use in free or paid games:",
    ],
    list: [
      "**pumpkin** (faces: classic, happy, scary, surprised, none), **gravestone** (rounded, cross, obelisk, slab, broken), **dead-tree**, **fence** (iron or picket), **house-kit** (a whole house of 1–3 floors, or single walls, doors, windows and roofs), **candles**, **lantern** (with a flickering light), **fog**, **bats**, **ghost**.",
      "For avatars: **pumpkin-head**, **witch-hat**, **skeleton-hoodie**.",
    ],
  },
  {
    paras: ["To use a piece in another project:"],
    code: ["thrdm install threedom/halloween-pumpkin --params '{\"face\":\"scary\"}'"],
  },
  {
    heading: "4. Put it on Banger",
    paras: ["Pick your game's name: set `slug` in `thrdm.json` (say, `my-haunt`). Then install `thrdm` and sign in, once:"],
    code: ["npm install -g https://banger.sh/downloads/thrdm-0.1.0.tgz", "thrdm login you@example.com"],
  },
  {
    paras: [
      "Banger emails you a sign-in link. **Copy the link, don't open it** (it works once), and paste it where `thrdm` asks. You don't need an npm account.",
      "Then build and deploy:",
    ],
    code: ["npm run build", "thrdm deploy dist"],
  },
  {
    paras: [
      "Your haunt is live at `https://my-haunt.{{builds-domain}}/`. The first deploy claims the name, so `my-haunt` is yours from then on. It joins Banger's catalogue once it passes review; until then the deploy says `review: pending`.",
      "Every deploy is a new version. If something breaks, go back:",
    ],
    code: ["thrdm versions my-haunt", "thrdm rollback my-haunt <version>"],
  },
  {
    heading: "5. Join the Halloween row",
    paras: [
      "{{rows: how a deployed game is marked as a haunt (tag, form field or dashboard toggle)}}",
      "Then share your link. Anyone who plays three haunts lights up their pumpkin head, so your players help everyone in the row.",
    ],
  },
  {
    heading: "Building with an AI agent",
    paras: [
      "The template comes with `AGENTS.md` and `CLAUDE.md`. They give a coding agent the file map (start with `src/haunt.js`), every param in the pack, and the rules: keep it light, spooky not gory, and never deploy without your yes.",
      "1. Open the template folder in your agent (Claude Code, Codex, or any agent that supports MCP).",
      "2. Sign in once with `thrdm login` (step 4), then add the thrdm MCP so the agent can use the pack and deploy for you. In Claude Code, from your project folder:",
    ],
    code: ["claude mcp add thrdm -- thrdm mcp"],
  },
  {
    paras: [
      "In Codex: `codex mcp add thrdm -- thrdm mcp`.",
      "3. Ask for what you want: *\"Make my haunted house: a foggy graveyard, three carved pumpkins, a ghost that drifts towards the player.\"*",
      "4. When it's ready, ask it to deploy. The agent shows you what it will upload first. Nothing goes up until you say yes.",
    ],
  },
  {
    heading: "Making an asset instead",
    paras: ["Halloween assets are static for now: a GLB file, its textures and a manifest. No code."],
    code: [
      "thrdm publish ./my-asset            # a dry run: checks it and shows triangles, size and problems",
      "thrdm publish ./my-asset --confirm  # sends it for review",
    ],
  },
  {
    paras: [
      "Your asset's manifest declares its own budget (triangles, draw calls, texture size), and the check holds it to that. It also has to render in our headless review.[[ {{haunt: where the manifest format is documented for outsiders}}]]",
    ],
  },
  { heading: "Got stuck?", paras: ["{{owner: where people ask for help (Discord, X, email)}}"] },
];

/** Lines that name a banned word only to say there's none of it (open-haunt.md's own rules). */
export const BANNED_OK = ["A showcase, not a contest", "No prizes, no judges, no winners."];

/** Words public copy never uses (docs/launch/README.md, open-haunt.md). */
export const BANNED = [
  /\bwin(s|ner|ners|ning)?\b/i,
  /\bprizes?\b/i,
  /\bcontest\b/i,
  /\bcompetition\b/i,
  /\bjudged\b/i,
  /\bjury pick\b/i,
  /\benter to\b/i,
  /\bThreedom\b/,
  /\bRust\b/,
  /\bV8\b/,
  /\bwgpu\b/i,
  /\bCEF\b/,
];
